/**
 * Обвязка приложения на телефоне: шапка сверху и разделы внизу.
 *
 * Разделы — под большим пальцем, как в любом мобильном приложении: верхняя
 * полоса компьютерной версии на 390 px не помещается и уезжала за край.
 * Больше пяти разделов (внешние разделы администратора) — четыре и «Ещё».
 *
 * Безопасные зоны iPhone: шапка отступает от выреза, панель — от полоски
 * «Домой» (env(safe-area-inset-*), нужен viewport-fit=cover в index.html).
 */

import { useEffect, useState } from 'react';
import {
  Bell,
  BellOff,
  Building2,
  Globe,
  LogOut,
  MessageCircle,
  MoreHorizontal,
  ScrollText,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { BucorMark } from './BucorMark.tsx';

export interface MobileNavItem {
  id: string;
  label: string;
  icon: 'telegram' | 'stays' | 'settings' | 'audit' | 'external';
  active: boolean;
  onSelect: () => void;
}

const ICONS: Record<MobileNavItem['icon'], LucideIcon> = {
  telegram: MessageCircle,
  stays: Building2,
  settings: Settings,
  audit: ScrollText,
  external: Globe,
};

const MAX_TABS = 5;

export function MobileBottomBar({ items }: { items: MobileNavItem[] }) {
  const [moreOpen, setMoreOpen] = useState(false);

  // Признак телефонной обвязки на <html>: по нему плашки «нет сети» и
  // «установить» встают над нижней панелью (tailwind.css, --tw-bottom-bar).
  useEffect(() => {
    document.documentElement.setAttribute('data-tw-mobile-shell', '');
    return () => document.documentElement.removeAttribute('data-tw-mobile-shell');
  }, []);
  const overflow = items.length > MAX_TABS;
  const tabs = overflow ? items.slice(0, MAX_TABS - 1) : items;
  const rest = overflow ? items.slice(MAX_TABS - 1) : [];
  const restActive = rest.some((i) => i.active);

  return (
    <>
      {moreOpen && (
        <div className="fixed inset-0 z-40 bg-black/30" onClick={() => setMoreOpen(false)}>
          <div
            className="absolute inset-x-3 rounded-2xl border border-border bg-card p-1.5 shadow-xl"
            style={{ bottom: 'calc(env(safe-area-inset-bottom) + 68px)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {rest.map((item) => {
              const Icon = ICONS[item.icon];
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    item.onSelect();
                    setMoreOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-xl border-0 px-3 py-3 text-left text-[15px] font-medium',
                    item.active ? 'bg-primary/10 text-brand' : 'bg-transparent text-heading',
                  )}
                >
                  <Icon className="size-5" />
                  <span className="truncate">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <nav
        className="tw-bottom-bar relative z-40 flex flex-none items-stretch border-t border-border bg-card/95 backdrop-blur-xl"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {tabs.map((item) => (
          <Tab key={item.id} label={item.label} Icon={ICONS[item.icon]} active={item.active} onClick={item.onSelect} />
        ))}
        {overflow && (
          <Tab label="Ещё" Icon={MoreHorizontal} active={restActive || moreOpen} onClick={() => setMoreOpen((v) => !v)} />
        )}
      </nav>
    </>
  );
}

function Tab({ label, Icon, active, onClick }: { label: string; Icon: LucideIcon; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex h-[60px] min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-none border-0 bg-transparent px-1 py-0 text-[11px] font-medium transition-colors',
        active ? 'text-brand' : 'text-muted-foreground',
      )}
    >
      <span className={cn('grid h-7 w-12 place-items-center rounded-full transition-colors', active && 'bg-primary/12')}>
        <Icon className="size-[21px]" strokeWidth={active ? 2.3 : 1.9} />
      </span>
      <span className="max-w-full truncate">{label}</span>
    </button>
  );
}

export function MobileHeader({
  title,
  user,
  notifications,
  onTestPush,
  onLogout,
}: {
  title: string;
  user: { name: string; role: string };
  notifications: { on: boolean; disabled: boolean; label: string; onToggle: () => void } | null;
  /** Пуши включены — в меню профиля «Проверить уведомления». */
  onTestPush?: () => void;
  onLogout: () => void;
}) {
  const [menu, setMenu] = useState(false);
  return (
    <header
      className="relative z-40 flex flex-none items-center gap-2.5 border-b border-white/40 bg-white/50 px-3.5 backdrop-blur-xl dark:border-white/5 dark:bg-white/[0.03]"
      style={{ paddingTop: 'env(safe-area-inset-top)', minHeight: 'calc(env(safe-area-inset-top) + 52px)' }}
    >
      <BucorMark className="size-7 flex-none" />
      <h1 className="min-w-0 flex-1 truncate text-[17px] font-semibold tracking-[-0.02em] text-heading">{title}</h1>
      {notifications && (
        <button
          type="button"
          onClick={notifications.onToggle}
          disabled={notifications.disabled}
          aria-label={notifications.label}
          aria-pressed={notifications.on}
          className={cn(
            'grid size-9 place-items-center rounded-full border-0 bg-transparent p-0 disabled:opacity-40',
            notifications.on ? 'text-brand' : 'text-muted-foreground',
          )}
        >
          {notifications.on ? <Bell className="size-5" /> : <BellOff className="size-5" />}
        </button>
      )}
      <button
        type="button"
        onClick={() => setMenu((v) => !v)}
        aria-label="Профиль"
        className="grid size-9 place-items-center rounded-full border-0 bg-primary/10 p-0 text-[13px] font-semibold text-brand"
      >
        {initials(user.name)}
      </button>
      {menu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setMenu(false)} />
          <div className="absolute right-3 top-full z-50 mt-1.5 w-60 rounded-2xl border border-border bg-card p-1.5 shadow-xl">
            <div className="px-3 py-2">
              <p className="truncate text-[14px] font-semibold text-heading">{user.name}</p>
              <p className="text-[12px] text-muted-foreground">{user.role}</p>
            </div>
            {notifications && (
              <p className="px-3 pb-1 text-[12px] text-muted-foreground">{notifications.label}</p>
            )}
            {onTestPush && (
              <button
                type="button"
                onClick={() => {
                  onTestPush();
                  setMenu(false);
                }}
                className="flex w-full items-center gap-2.5 rounded-xl border-0 bg-transparent px-3 py-2.5 text-left text-[14px] font-medium text-heading"
              >
                <Bell className="size-4" /> Проверить уведомления
              </button>
            )}
            <button
              type="button"
              onClick={onLogout}
              className="flex w-full items-center gap-2.5 rounded-xl border-0 bg-transparent px-3 py-2.5 text-left text-[14px] font-medium text-destructive hover:bg-destructive/10"
            >
              <LogOut className="size-4" /> Выйти
            </button>
          </div>
        </>
      )}
    </header>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '·';
}

const PROMPT_KEY = 'tw.push.promptDismissed';

/**
 * Предложение включить пуши — в установленном приложении, пока они
 * выключены. Разрешение спрашивается только по нажатию: iPhone иначе не
 * покажет запрос, а незапрошенный вопрос люди обычно запрещают навсегда.
 */
export function PushPrompt({ onEnable }: { onEnable: () => void }) {
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(PROMPT_KEY) === '1';
    } catch {
      return false;
    }
  });
  if (hidden) return null;
  const close = () => {
    setHidden(true);
    try {
      localStorage.setItem(PROMPT_KEY, '1');
    } catch {
      // не запомнится — покажется ещё раз
    }
  };
  return (
    <div className="flex flex-none items-center gap-2.5 border-b border-border bg-primary/5 px-3.5 py-2 text-[13px] text-heading">
      <Bell className="size-4 flex-none text-brand" />
      <p className="min-w-0 flex-1 leading-snug">Получать уведомления о новых сообщениях на этот телефон?</p>
      <button
        type="button"
        onClick={() => {
          onEnable();
          close();
        }}
        className="h-8 flex-none rounded-full border-0 bg-primary px-3 text-[13px] font-medium text-primary-foreground"
      >
        Включить
      </button>
      <button type="button" aria-label="Не сейчас" onClick={close} className="grid size-7 flex-none place-items-center rounded-full border-0 bg-transparent p-0 text-muted-foreground">
        ×
      </button>
    </div>
  );
}
